import React from 'react';
import { theme } from '../theme';
import AntwortTexte from './AntwortTexte';
import VokabularVerwaltung from './VokabularVerwaltung';
import { ownsEditableReplyProse, ownsEditableVocabulary } from '../services/roleRegistry';

// <purpose>The "Texte & Vokabular" tab — ONE editing surface for everything a tenant admin may
// re-word without a deploy. It hosts sections rather than being one screen: the reply-prose
// section (prompt overlay) and the vocabulary section (classification-wording overlay). Both ride
// the same engine (useRevisionedDrafts) and the same visual dialect (overlayEditorChrome), which
// is what makes them sections of one tab instead of two tabs that happen to look alike.</purpose>
// <dependencies>roleRegistry's capability predicates (the section gates); AntwortTexte;
// VokabularVerwaltung.</dependencies>
// <constraints>SECTIONS GATE BY CAPABILITY, NEVER BY ROLE NAME. Each predicate is derived from the
// backend's own per-role contract projection (roleRegistry.ROLE_CONTRACTS, pinned equal to
// deploy/projections/role-capabilities.json) — /config serves the tenant's role, the projection turns
// that into what the tenant's contract OWNS, and only that decides what renders here. No role
// literal appears in this file or at any call site.
//
// ⚠ The prose section's capability is "carries editable-SAFE reply rows", NOT "carries reply
// rows". Measured 2026-08-11 across the git-homed and DB-only prompt corpus: MSB roles carry 42
// reply-prompt rows, of which ZERO are customeradmin-editable-safe — every one is an internal LLM
// instruction (per-category task, classification rules, output format, persona, FAQ, business
// rules), and exposing those for editing is exactly what the safe-facts doctrine forbids. So MSB
// gates OUT of prose and IN for vocabulary, and the older "reply components are a VNB-role
// concept" reading is corrected: editable reply prose is vnb-only TODAY because MSB's prose is
// internal-only, not because the concept is vnb-shaped. The MSB sign-off block's safe, fact-level
// surface is the CUSTOMER_* config keys, which already have the Firmendaten tab.
//
// ⚠ The VOCABULARY section is offered to every known contract (widened 2026-08-11): its capability
// is "serves a vocabulary", and core applies the wording overlay once, outside its contract switch,
// so all three contracts serve one. The predicate it reads used to name the MsbVocabulary kind —
// a mid-transition scope written as though it were a structural property. Consequence for this
// file: the empty-state fallback below is now reachable ONLY for a role that never resolved.
//
// The tab itself is only offered when at least one section applies (see
// hostsTextOrVocabularyEditing at the TAB_DEFS gate) — this component never renders an empty
// shell, and the fallback below exists only for the impossible case where that gate is bypassed.
//
// ⚠ A THIRD section — "Oberfläche" — existed briefly (Task 10, part 4b, 2026-09-24 to 2026-09-28),
// editing the words the surface itself wears for a state (a decision, a sender's registry status)
// rather than a classification value. Operator ruling R36.6 (option B, 2026-09-28) DELETED it
// outright: those five state-word families stay SERVED (one source, the ban, the export reading
// one word) and are no longer admin-editable anywhere in the receiver. A per-tenant re-wording now
// goes through the institutional overlay layer (a customer migration), never a UI card. This file
// is back to two sections.
// </constraints>

const TexteUndVokabular: React.FC<{
  role: string;
  // Threaded through to the prose section's frozen cards, which link back to Firmendaten via
  // EinstellungenSeite's own in-page tab routing.
  onNavigateToFirmendaten?: () => void;
}> = ({ role, onNavigateToFirmendaten }) => {
  const showProse = ownsEditableReplyProse(role);
  const showVocabulary = ownsEditableVocabulary(role);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
      {showProse && <AntwortTexte onNavigateToFirmendaten={onNavigateToFirmendaten} />}
      {showVocabulary && <VokabularVerwaltung />}
      {!showProse && !showVocabulary && (
        <div style={{ color: theme.colors.textSecondary, fontSize: 13 }}>
          Für diese Rolle sind keine bearbeitbaren Texte oder Bezeichnungen hinterlegt.
        </div>
      )}
    </div>
  );
};

export default TexteUndVokabular;
